'use client';

import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Clock, TrendingUp } from 'lucide-react';

interface SearchResultsHeaderProps {
  searchQuery: string;
  totalCount: number;
  questionCount: number;
  storyCount: number;
  searchSource: 'elasticsearch' | 'traditional';
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  isLoading?: boolean;
}

export const SearchResultsHeader = ({
  searchQuery,
  totalCount,
  questionCount,
  storyCount,
  searchSource,
  sortBy = 'date',
  onSortChange,
  isLoading = false,
}: SearchResultsHeaderProps) => {
  const sortOptions = [
    { value: 'trending', label: 'Trending', icon: TrendingUp },
    { value: 'date', label: 'Date', icon: Clock },
  ];

  // Check if we have a search query
  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-[#24292E]">
            {hasSearchQuery ? (
              <>Search Results for "{searchQuery}"</>
            ) : (
              'All Questions & Stories'
            )}
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-600">
            {totalCount} result{totalCount !== 1 ? 's' : ''} found
          </p>
        </div>

        {!isLoading && onSortChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Sort by:</span>
            <Select value={sortBy} onValueChange={onSortChange}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-3 w-3" />
                        {option.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
};
